import { Component, Prop, State, Host, Element, h, Method, Watch } from '@stencil/core';
import { FormControlValidationState } from './_interfaces/FormControlValidationState';
import { ValidationStateMessage } from './_interfaces/ValidationStateMessage';
import { ValidatorFunction } from './_interfaces/ValidatorFunction';
import HTMLFieldDecoratorElement from '../_interfaces/HTMLFieldDecoratorElement';
import FormControl from '../_interfaces/FormControl';
import FieldValidator from './_interfaces/FieldValidator';
import defaultValidator from './_default-validator';
import warn from '../../../../capabilities/warn';
import directChildren from '../../../../capabilities/direct-children/direct-children';
import appendDescribedBy from '../_util/appendDescribedBy';

/**
 * hp6-inline-validator
 *
 * @description form-kit: Manages the validation state of a form control. Displays the
 * validation message below the slot. NOTE: Do not nest inline-validators.
 * @use
 * @formkit
 * @since 6.0.0
 * @slot - `default slot`: The unordered list of validation result messages to be displayed.
 */
@Component({
  tag: 'hp6-inline-validator',
  styleUrl: 'inline-validator.scss',
  shadow: false, // NOTE: must be shadow: false so that validation message can be read by screen readers
})
export class InlineValidator implements FieldValidator {
  /**
   * Error message to display on input validation failure
   */
  @Prop() errorMessage: string;

  /**
   * Success message to display on input validation success. Success validation
   * status and success message only displayed if successMessage is set.
   */
  @Prop() successMessage: string;

  /**
   * Alignment of the message.
   */
  @Prop() alignmentSelection: 'left' | 'center' = 'left'; // used only by CSS

  /**
   * If the FormControl in this slot is to be validated on losing focus
   */
  @Prop() validateOnBlur: boolean = true;

  /**
   * If the FormControl should be validated on first load
   */
  @Prop() validateOnLoad: boolean = false;

  /**
   * The ValidatorFunction to use. Can only be set via Javascript.
   * Default validation returns native HTML 5 element validity
   */
  @Prop() validator: ValidatorFunction = defaultValidator;

  /**
   * Indicates whether the enclosed FormControl is valid
   */
  @State() validationState: FormControlValidationState = new FormControlValidationState();

  /**
   * A reference to the <host /> element
   */
  @Element() host: HTMLElement;

  /**
   * constructor()
   */
  constructor() {
    this.handleElementEvent = this.handleElementEvent.bind(this);
    this.validate = this.validate.bind(this);
  }

  /**
   * Get the FormControl which is being validated.
   * Expected to be contained within this validator.
   */
  get formControl(): FormControl {
    const { host } = this;

    const formControl
      = host.querySelector('fieldset')
        || host.querySelector('input')
        || host.querySelector('select')
        || host.querySelector('textarea');

    if (!formControl) {
      warn('inline-validator get formControl() - formControl not found');
      return null;
    }

    if (!formControl.id) {
      warn('inline-validator get formControl() - formControl has no id');
    }

    return formControl;
  }

  /**
   * The id of the message element
   */
  get messageId(): string {
    return `${this.formControl.id}-inline-validator`;
  }

  /**
   * Attempt to find the decorator for the formControl.
   * Must be a direct child of this component and a parent of the formControl
   *
   * @returns decorator: HTMLFieldDecorator
   */
  get decorator(): HTMLFieldDecoratorElement {
    let decorator: HTMLFieldDecoratorElement;

    const decorators = directChildren(this.host, '[data-decorator]');
    if (decorators.length > 0) {
      // if other decorators are found, only return the first
      decorator = decorators[0] as HTMLFieldDecoratorElement;
    }

    if (decorator) {
      const { formControl } = this;
      const closestDecorator = formControl.closest('[data-decorator]') as HTMLElement;

      if (closestDecorator && closestDecorator === decorator) {
        return decorator;
      }
    }

    return null;
  }

  /**
   * If errorMessage or successMessage changes, re-validate and re-render
   * Required for inline-declarative-validator to work
   *
   * @returns
   */
  @Watch('errorMessage')
  @Watch('successMessage')
  async watchMessages() {
    await this.validate();
  }

  /**
   * Return formControl.validity - created as a static method so this can be mocked in tests.
   * Jest/Puppeteer MockInputElement does not have a validity property.
   * Mock this static function in tests to get around that
   */
  static getFormControlValidity(formControl: FormControl): ValidityState {
    return formControl.validity;
  }

  /**
   * Updates the validationState by running the validator function
   *
   * @returns ValidityState
   */
  @Method()
  async validate(): Promise<FormControlValidationState> {
    const { formControl, successMessage, errorMessage } = this;

    const formControlValidity = InlineValidator.getFormControlValidity(formControl);

    const validationStateMessage: ValidationStateMessage = await this.validator({ formControl });
    if (validationStateMessage.isValid !== formControlValidity.valid) {
      warn(
        `hp6-inline-validator: validator result does not match native validity. Does your custom validator setCustomValidity()?`,
      );
    }

    if (!validationStateMessage) {
      return Promise.resolve(new FormControlValidationState());
    }

    const { isValid, message: newMessage } = validationStateMessage;

    let message: string = newMessage;

    // No message if isValid is undefined (ie. not yet validated)
    if (isValid === true) {
      if (!newMessage) {
        if (successMessage) {
          message = successMessage;
        }
      }
    } else if (isValid === false) {
      message = formControlValidity.customError ? newMessage : errorMessage || newMessage;
    }

    this.validationState = new FormControlValidationState({
      isValid,
      message,
    });

    return Promise.resolve(this.validationState);
  }

  /**
   * Validate the formControl when it emits an event
   *
   * Note: `focusout` with an event.relatedTarget outside the formControl is functionally
   * the same as `blur` with the benefit of working for fieldsets as well (`blur` does
   * not bubble, while `focusout` does).
   *
   * Only validate when the relatedTarget (where focus is going next) is outside the host.
   */
  async handleElementEvent(event: FocusEvent) {
    const { validateOnBlur, formControl } = this;

    const parentInlineValidator = this.host.parentElement.closest('hp6-inline-validator');

    // Do not validate if this is a nested inline-validator
    if (parentInlineValidator) {
      return;
    }

    if (!validateOnBlur) {
      return;
    }

    const { type } = event;
    const relatedTarget = event.relatedTarget as HTMLElement;

    /*
     */
    if (
      type === 'focusout'
      && !formControl.contains(relatedTarget)
      && formControl !== relatedTarget
    ) {
      await this.validate();
    }
  }

  /**
   * Sets isValid on the decorator, only if this is inline-validator is not nested
   * inside another.
   * Watches for change in this.validationState
   */
  @Watch('validationState')
  updateDecorator() {
    const parentInlineValidator = this.host.parentElement.closest('hp6-inline-validator');

    // Do not set decorator isValid if this is a nested inline-validator
    if (!parentInlineValidator) {
      const { decorator } = this;
      if (!decorator) {
        warn('inline-validator: no decorator found');
        return;
      }

      const { validationState } = this;
      const { isValid } = validationState;

      let isValidNew;

      if (isValid === true) {
        // only validate true of this.successMessage is set
        const { successMessage } = this;

        if (successMessage) {
          isValidNew = true;
        }
      } else if (isValid === false) {
        isValidNew = false;
      }

      decorator.isValid = isValidNew;
    }
  }

  /**
   * Boolean indicating if the formControl has been validated
   */
  get isValidated(): boolean {
    const { validationState } = this;
    return validationState.isValid !== undefined;
  }

  /**
   * Stencil lifecycle
   */
  componentDidLoad() {
    const { formControl, messageId } = this;

    if (!formControl) {
      warn('inline-validator componentDidLoad() - formControl not found');
      return;
    }

    appendDescribedBy(formControl, messageId);

    if (this.validateOnLoad) {
      this.validate();
    }

    formControl.addEventListener('focusout', this.handleElementEvent);
  }

  /**
   * Stencil lifecycle
   */
  disconnectedCallback() {
    const { formControl } = this;

    if (!formControl) {
      warn('inline-validator disconnectedCallback() - formControl not found');
      return;
    }

    formControl.removeEventListener('focusout', this.handleElementEvent);
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    const { formControl, isValidated, validationState } = this;

    if (!formControl) {
      warn('inline-validator render() formControl not found');
      return null;
    }

    const { message, isValid } = validationState;

    return (
      <Host>
        <slot />
        <div class="validation-message" id={`${formControl.id}-inline-validator`} aria-live="polite">
          {isValidated && message
            ? (
                <span class={isValid ? 'valid' : 'invalid'}>
                  {message}
                </span>
              )
            : null}
        </div>
      </Host>
    );
  }
}
